import React, { type ComponentProps, type ReactElement, type ReactNode } from "react"
import { CheckIcon, XIcon } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"
import { useIsMobile } from "@/lib/hooks/useMediaQuery"
import { Button, type ButtonProps } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTopBar,
} from "@/components/ui/sheet"

/**
 * Minden dialógus héja: asztalon középre igazított `Dialog`, `md` alatt alulról
 * felcsúszó vaul `Sheet`. A hívó csak a tartalmat adja (`title`, `description`,
 * `children`, `footer`), a szerkezet - fejléc, görgethető törzs, lábléc - itt van
 * egy helyen. Mobilon a lábléc elsődleges és mégsem gombja a fejléc ✓ / ✕ ikonjává
 * válik (lásd `splitMobileFooter`), a többi gomb a lábléc marad.
 */
interface ResponsiveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /**
   * A dialógus leírása. Alapból `sr-only` (csak képernyőolvasónak), a látható
   * magyarázó szöveg ilyenkor a `children`-be megy. SZÖVEG-szintű tartalom legyen
   * (`span`, `br`, `strong`): a primitív `<p>`-t renderel, block elem (`div`, `p`)
   * benne érvénytelen HTML - az a `children` dolga.
   */
  description?: ReactNode
  /**
   * A leírás LÁTHATÓAN is jelenjen meg, a törzs tetején (törzs nélkül a törzs
   * helyén). Így a szöveg egyszerre marad `Description` primitív (az
   * `aria-describedby` bekötve, a képernyőolvasó megnyitáskor felolvassa) és látható
   * magyarázat. A megerősítők ezt használják.
   */
  descriptionVisible?: boolean
  /**
   * A törzs tartalma. Lehet üres (`null`): a megerősítőknél a látható leírás az
   * egyetlen tartalom, a gombok a `footer`-ben ülnek - ilyenkor a törzs konténere
   * sem renderel, különben az üres `py-4`-e kétszeres alsó levegőt adna.
   */
  children: ReactNode
  footer?: ReactNode
  /** Opcionális keresőmező a fejléc alatt, saját elválasztó vonallal. */
  search?: ReactNode
  /** Az asztali `DialogContent` extra osztálya (jellemzően a szélesség: `sm:max-w-2xl`). */
  className?: string
  titleClassName?: string
  /**
   * A görgethető törzs belső wrapperének osztálya. Alapból `px-4 py-4`; add meg
   * (pl. `p-0`), ha széltől szélig érő tartalom kell.
   */
  bodyClassName?: string
  /** Nem görgető, a törzs alatti tartalom (pl. „új létrehozása" sor). */
  afterBody?: ReactNode
  /** Görgetés a törzs konténerén (pl. végtelenített listákhoz). */
  onContentScroll?: React.UIEventHandler<HTMLDivElement>
  initialFocus?: ComponentProps<typeof DialogContent>["initialFocus"]
  /**
   * Ha igaz, mobilon is a középre igazított `Dialog` jelenik meg (nem alsó Sheet).
   * Akkor kell, ha a hívó egy Base UI modális `Dialog` belsejében van: ott a
   * beágyazott vaul Sheet koppintása „kívülre kattintásnak" számít, és bezárná a
   * szülőt. Sheeten belül nem kell: a beágyazást a `Sheet` `NestedRoot`-ja kezeli.
   */
  forceDialog?: boolean
  /**
   * A jobb felső ✕ bezáró gomb. Blokkoló dialógusnál (pl. kötelező elfogadás) add
   * `false`-ra - ekkor mobilon is középre igazított `Dialog` renderel (az alsó Sheet
   * húzással elhúzható lenne), így a „nem bezárható" szándék mindkét felületen áll.
   */
  showCloseButton?: boolean
}

type FooterButtonElement = ReactElement<ButtonProps, typeof Button>

function flattenFooterChildren(node: ReactNode): ReactNode[] {
  return React.Children.toArray(node).flatMap((child) => {
    // Fragmenteket és sima <div> wrappereket is kibontunk, hogy a bennük lévő
    // Mégsem / elsődleges gombot a fejlécbe lehessen emelni (különben a szétválasztás
    // nem talál semmit, és mobilon dupla „Mégsem" lenne: lábléc gomb + fejléc ✕).
    if (
      React.isValidElement(child) &&
      (child.type === React.Fragment || child.type === "div")
    ) {
      return flattenFooterChildren((child.props as { children?: ReactNode }).children)
    }
    return [child]
  })
}

/**
 * Explicit lábléc-szerep: `data-footer-role="primary" | "primary-neutral" | "cancel"
 * | "tertiary" | "destructive"`. Ha a hívó megadja, ez nyer a variant-heurisztika
 * felett - így egy `outline` „harmadlagos" gomb (pl. Szerkesztés egy részletező
 * láblécében) a láblécben maradhat, és nem a ✕ lesz belőle. Megadás nélkül a
 * heurisztika dönt.
 *
 * `primary-neutral`: mint a `primary` (mobilon a fejléc ✓-ja lesz), de a ✓ semleges,
 * szürke - nem mentő/jóváhagyó akcióhoz (pl. státuszváltó). `tertiary` és
 * `destructive` a láblécben marad; a `destructive` asztalon balra igazítódik
 * (egységes „törlés balra" minta a részletezőkben).
 */
type FooterRole = "primary" | "primary-neutral" | "cancel" | "tertiary" | "destructive"

function getFooterRole(node: ReactNode): FooterRole | undefined {
  if (!React.isValidElement(node)) return undefined
  const role = (node.props as Record<string, unknown>)["data-footer-role"]
  return role === "primary" ||
    role === "primary-neutral" ||
    role === "cancel" ||
    role === "tertiary" ||
    role === "destructive"
    ? role
    : undefined
}

function isPrimaryFooterButton(node: ReactNode): node is FooterButtonElement {
  if (!React.isValidElement(node) || node.type !== Button) return false
  const role = getFooterRole(node)
  if (role) return role === "primary" || role === "primary-neutral"
  const variant = (node.props as ButtonProps).variant
  // A destruktív megerősítő gomb is „primary": a csak-destruktív dialógusoknál
  // (Törlés, Lemondás) így a megerősítés a fejléc ✓-jára kerül - különben a ✓ helye
  // üres maradna, és a destruktív gomb magában lógna a láblécben.
  return variant === undefined || variant === "default" || variant === "destructive"
}

function isCancelFooterButton(node: ReactNode): node is FooterButtonElement {
  if (!React.isValidElement(node) || node.type !== Button) return false
  const role = getFooterRole(node)
  if (role) return role === "cancel"
  // A „Mégsem" gombot a VARIANT alapján ismerjük fel (outline / ghost), nem a feliratból
  // - az törékeny lenne. Az ELSŐ ilyen gomb a mégsem (a láblécben konvenció szerint a
  // másodlagos gomb áll elöl). Puszta „Bezárás" gomb nincs: a bezárót a fejléc adja.
  const variant = (node.props as ButtonProps).variant
  return variant === "outline" || variant === "ghost"
}

function extractText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(extractText).join(" ").trim()
  if (React.isValidElement(node)) {
    return extractText((node.props as { children?: ReactNode }).children)
  }
  return ""
}

/**
 * A fejlécbe emelt gombra a saját megjelenését (felirat, méret, variáns) nem visszük át
 * - ott ikon-gomb lesz belőle -, a viselkedését (`onClick`, `disabled`, `type`, `form`)
 * viszont igen. Ez a lista mondja ki, mi marad ki.
 */
const PRESENTATION_PROPS = ["className", "children", "render", "size", "variant", "loading"] as const

function behaviourProps(props: ButtonProps): Omit<ButtonProps, (typeof PRESENTATION_PROPS)[number]> {
  const rest = { ...props }
  for (const key of PRESENTATION_PROPS) delete rest[key]
  return rest
}

function splitMobileFooter(footer?: ReactNode): {
  footer: ReactNode
  action: ReactNode
  close: ReactNode
} {
  if (!footer) return { footer, action: null, close: null }

  const children = flattenFooterChildren(footer)
  // Csupa feltételes gombból álló lábléc üresen is „létezik" (a fragment igaz értékű),
  // ezért itt dől el, hogy egyáltalán van-e lábléc - lásd a `hasFooter`-t alább.
  if (children.length === 0) return { footer: null, action: null, close: null }

  let primaryIndex = -1
  for (let index = children.length - 1; index >= 0; index -= 1) {
    if (isPrimaryFooterButton(children[index])) {
      primaryIndex = index
      break
    }
  }

  let cancelIndex = -1
  for (let index = 0; index < children.length; index += 1) {
    if (isCancelFooterButton(children[index])) {
      cancelIndex = index
      break
    }
  }

  if (primaryIndex === -1 && cancelIndex === -1) return { footer, action: null, close: null }

  const primaryButton = primaryIndex >= 0 ? (children[primaryIndex] as FooterButtonElement) : null
  const cancelButton = cancelIndex >= 0 ? (children[cancelIndex] as FooterButtonElement) : null
  // Csak a fejlécbe emelt két gombot vesszük ki (INDEX szerint): ha több outline gomb
  // van, csak az első (a mégsem) megy fel, a többi a mobil láblécben marad.
  const remainingFooter = children.filter(
    (_child, index) => index !== primaryIndex && index !== cancelIndex
  )

  const action = primaryButton
    ? (() => {
        const {
          children: buttonChildren,
          variant: primaryVariant,
          loading: primaryLoading,
          disabled: primaryDisabled,
        } = primaryButton.props
        const buttonProps = behaviourProps(primaryButton.props)
        const label = extractText(buttonChildren) || "Alkalmaz"
        // `primary-neutral` → szürke ✓ (mint a bal oldali ✕). Csak-destruktív
        // megerősítőnél (Törlés, Lemondás) a ✓ is destruktív, hogy ne tűnjön
        // jóváhagyó akciónak; minden más esetben elsődleges színű.
        const isNeutralAction = getFooterRole(primaryButton) === "primary-neutral"
        const actionVariant = isNeutralAction
          ? "secondary"
          : primaryVariant === "destructive"
            ? "destructive"
            : "default"

        return (
          <Button
            {...buttonProps}
            variant={actionVariant}
            size="icon-lg"
            // Betöltés alatt a ✓ helyett pörgő ikon: a gomb saját `loading`
            // megjelenítését nem használjuk, mert a feliratot a szétválasztás eldobja.
            disabled={primaryDisabled || primaryLoading}
            className="rounded-full transition-transform hover:scale-110 active:scale-95"
            aria-label={buttonProps["aria-label"] ?? label}
          >
            {primaryLoading ? (
              <Spinner aria-hidden="true" role="presentation" aria-label={undefined} />
            ) : (
              <CheckIcon weight="bold" />
            )}
            <span className="sr-only">{label}</span>
          </Button>
        )
      })()
    : null

  const close = cancelButton
    ? (() => {
        const buttonProps = behaviourProps(cancelButton.props)
        const label = extractText(cancelButton.props.children) || "Mégsem"

        return (
          <Button
            {...buttonProps}
            variant="secondary"
            size="icon-lg"
            className="rounded-full transition-transform hover:scale-110 active:scale-95"
            aria-label={buttonProps["aria-label"] ?? label}
          >
            <XIcon />
            <span className="sr-only">{label}</span>
          </Button>
        )
      })()
    : null

  return {
    footer: remainingFooter.length > 0 ? <>{remainingFooter}</> : null,
    action,
    close,
  }
}

/**
 * A görgethető törzs mindkét ágon ugyanaz (asztali `Dialog`, mobil `Sheet`):
 * `min-h-0 flex-1` adja neki a fejléc és a lábléc közti maradék helyet, az
 * `overscroll-contain` pedig a törzs végén megállítja a NATÍV görgetés-láncolást
 * (a mögötte lévő lap gumiszalag-effektje), így a mögötte lévő elmosott háttér nem
 * rajzolódik újra. A fiók húzással záródására nincs hatással: azt a vaul
 * pointer-eseményekből vezeti, nem görgetés-láncolásból.
 */
const BODY_SCROLL_CLASS = "min-h-0 flex-1 overflow-y-auto overscroll-contain"

export function ResponsiveDialog({
  open,
  onOpenChange,
  title,
  description,
  descriptionVisible = false,
  children,
  footer,
  search,
  className,
  titleClassName,
  bodyClassName,
  afterBody,
  onContentScroll,
  initialFocus,
  forceDialog = false,
  showCloseButton = true,
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile()
  const mobileFooter = splitMobileFooter(footer)
  // A lábléc ugyanúgy elmarad, ha nincs mit mutatnia, mint a törzs: egy csupa feltételes
  // gombot tartalmazó fragment igaz értékű, de üresen csak egy elválasztó vonalat és
  // levegőt adna a dialógus alján.
  const hasFooter = flattenFooterChildren(footer).length > 0
  // Nyitáskor a cím kapja a fókuszt, nem a törzs első fókuszálható eleme. Utóbbi egy
  // hosszú dialógusban (a foglalás részletei: másoló gombok a lap közepén) a
  // megnyitás pillanatában legörgetné a törzset, a képernyőolvasó pedig a cím helyett
  // egy gomb feliratát mondaná be; mobilon ugyanezt a `SheetContent` konténer-fókusza
  // oldja meg. A hívó felülbírálhatja az `initialFocus` proppal.
  const titleRef = React.useRef<HTMLHeadingElement>(null)
  // Üres törzs (a megerősítők `{null}`-lal hívnak) mellett a törzs wrapper nem
  // renderel. A `Children.toArray` azért kell a sima null-vizsgálat helyett, mert a
  // feltételes törzs jellemzően fragmentben érkezik (`<>{a && <X/>}</>`): azt a
  // toArray lapítja, és a kiszűrt null/false után üres tömböt ad.
  const hasBody = React.Children.toArray(children).length > 0
  // A látható leírás is a Description primitív marad (nem külön div a törzsben), hogy az
  // `aria-describedby` bekötése megmaradjon. Törzs MELLETT a görgethető törzs tetejére
  // kerül, és ugyanúgy elgörög, mint a többi tartalom: a fejléc alatt, a törzsön kívül
  // egy odaragadt sáv lenne, ami alá becsúszna a görgetett tartalom. Törzs NÉLKÜL - a
  // megerősítőknél a leírás az egyetlen tartalom - marad a törzs helyén: ő maga
  // zsugorodik és görget, különben egy hosszú szöveg alacsony ablakban kitolná a
  // láblécet a levágott peremen túlra.
  const descriptionInBody = !!description && descriptionVisible && hasBody
  const descriptionOutsideBody = !!description && !descriptionInBody
  const descriptionClassName = !descriptionVisible
    ? "sr-only"
    : descriptionInBody
      ? "not-sr-only mb-4 text-sm text-muted-foreground"
      : "not-sr-only min-h-0 shrink overflow-y-auto overscroll-contain px-4 py-4 text-sm text-muted-foreground"

  // showCloseButton={false} (blokkoló dialógus) → mindig középre igazított Dialog: a
  // mobil SheetTopBar mindig mutat bezárót, és az alsó Sheet húzással elhúzható.
  const asDialog = forceDialog || !showCloseButton

  if (isMobile && !asDialog) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85vh] rounded-t-xl" initialFocus={initialFocus}>
          <SheetTopBar
            className="shrink-0 border-b"
            title={title}
            titleClassName={titleClassName}
            /* A saját ✕ neve „Bezárás", mint asztalon: ez a semleges bezáró. „Mégsem"
               csak akkor kerül rá, ha egy űrlap mégsem gombja emelkedett ide - annak a
               feliratát a `splitMobileFooter` viszi magával. */
            closeLabel="Bezárás"
            close={mobileFooter.close}
            onClose={() => onOpenChange(false)}
            action={mobileFooter.action}
          />
          {descriptionOutsideBody && (
            <SheetDescription className={descriptionClassName}>{description}</SheetDescription>
          )}
          {search && <div className="shrink-0 border-b px-4 py-3">{search}</div>}
          {hasBody && (
            <div className={BODY_SCROLL_CLASS} onScroll={onContentScroll}>
              <div className={cn("px-4 py-4", bodyClassName)}>
                {descriptionInBody && (
                  <SheetDescription className={descriptionClassName}>
                    {description}
                  </SheetDescription>
                )}
                {children}
              </div>
            </div>
          )}
          {afterBody}
          {mobileFooter.footer && (
            <SheetFooter className="shrink-0 flex-col-reverse border-t px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {mobileFooter.footer}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0", className)}
        initialFocus={initialFocus ?? titleRef}
        showCloseButton={showCloseButton}
      >
        {/* A bezáró ✕ a jobb felső sarokban lebeg (abszolút), a fejléc alá futna: a jobb
            oldali padding tartja neki a helyet, hogy egy hosszú cím ne csússzon alá. */}
        <DialogHeader className={cn("shrink-0 border-b py-4 pl-4", showCloseButton ? "pr-14" : "pr-4")}>
          {/* `leading-normal`: a DialogTitle alap `leading-none`-ja pont betűmagasságú
              sordobozt ad, amiből a lelógó szárak (g, y) többsoros címnél levágódnának. */}
          <DialogTitle
            ref={titleRef}
            tabIndex={-1}
            className={cn("leading-normal outline-none", titleClassName)}
          >
            {title}
          </DialogTitle>
        </DialogHeader>
        {descriptionOutsideBody && (
          <DialogDescription className={descriptionClassName}>{description}</DialogDescription>
        )}
        {search && <div className="shrink-0 border-b px-4 py-3">{search}</div>}
        {hasBody && (
          <div className={BODY_SCROLL_CLASS} onScroll={onContentScroll}>
            <div className={cn("px-4 py-4", bodyClassName)}>
              {descriptionInBody && (
                <DialogDescription className={descriptionClassName}>
                  {description}
                </DialogDescription>
              )}
              {children}
            </div>
          </div>
        )}
        {afterBody}
        {/* A `data-footer-role="destructive"` gomb asztalon balra (order-first + mr-auto),
            a többi akció jobbra - egységes „törlés balra" minta. */}
        {hasFooter && (
          <DialogFooter className="mx-0 mb-0 shrink-0 gap-2 border-t px-4 py-4 [&_[data-footer-role=destructive]]:sm:order-first [&_[data-footer-role=destructive]]:sm:mr-auto">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
